// repoCartographerClient — browser-side wrapper around the
// repo-cartographer edge function. Called when the user toggles
// "AI-assisted" in the RepoUploadDialog.
//
// On success: returns a parsed MultiSheetWorkflowState ready for
//             onImport().
// On failure: throws — caller falls back to the deterministic engine.

import { supabase } from '@/integrations/supabase/client';
import { buildAiSystemPrompt } from '@/lib/cartographer/aiPrompt';
import type { MultiSheetWorkflowState } from '@/types/workflow';

export interface RepoCartographerResult {
  workflow: MultiSheetWorkflowState;
  providerKind: string;
  model: string;
  usage: { inputTokens: number; outputTokens: number };
  grounded: unknown;
}

export class RepoCartographerError extends Error {
  constructor(public code: string, public detail?: string, public status?: number) {
    super(`${code}${detail ? `: ${detail}` : ''}`);
    this.name = 'RepoCartographerError';
  }
}

/**
 * Strip ```json fences if the model emitted them, and parse.
 * Returns null when no valid JSON is found.
 */
function tryParseLeanJson(raw: string): MultiSheetWorkflowState | null {
  let text = raw.trim();
  // Strip markdown fences if present
  const fenceMatch = text.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/);
  if (fenceMatch) text = fenceMatch[1].trim();
  try {
    const obj = JSON.parse(text);
    if (typeof obj !== 'object' || obj === null) return null;
    return obj as MultiSheetWorkflowState;
  } catch {
    return null;
  }
}

async function readInvokeError(error: unknown): Promise<{ message: string; detail?: string; status?: number }> {
  const message = error instanceof Error ? error.message : String(error);
  const context = (error as { context?: unknown } | null)?.context;
  if (!(context instanceof Response)) return { message };

  const status = context.status;
  const raw = await context.text().catch(() => '');
  if (!raw) return { message, status };
  try {
    const body = JSON.parse(raw) as { error?: string; detail?: string; status?: number; correlationId?: string };
    const code = body.error ? `${message} (${body.error})` : message;
    const detail = [body.detail, body.correlationId ? `cid=${body.correlationId}` : undefined].filter(Boolean).join(' · ');
    return { message: code, detail: detail || raw.slice(0, 240), status: body.status ?? status };
  } catch {
    return { message, detail: raw.slice(0, 240), status };
  }
}

export async function callRepoCartographer(opts: {
  files: ReadonlyArray<string>;
  repoName: string;
}): Promise<RepoCartographerResult> {
  const systemPrompt = buildAiSystemPrompt();

  const { data, error } = await supabase.functions.invoke('repo-cartographer', {
    body: {
      files: opts.files,
      repoName: opts.repoName,
      systemPrompt,
    },
  });

  if (error) {
    const parsed = await readInvokeError(error);
    throw new RepoCartographerError('invoke_failed', parsed.detail ?? parsed.message, parsed.status);
  }
  if (!data || typeof data !== 'object') {
    throw new RepoCartographerError('invalid_response');
  }

  const d = data as {
    ok?: boolean;
    text?: string;
    providerKind?: string;
    model?: string;
    usage?: { inputTokens?: number; outputTokens?: number };
    grounded?: unknown;
    error?: string;
    detail?: string;
    status?: number;
  };

  if (d.error) {
    throw new RepoCartographerError(d.error, d.detail, d.status);
  }
  if (!d.ok || typeof d.text !== 'string') {
    throw new RepoCartographerError('empty_response');
  }

  const workflow = tryParseLeanJson(d.text);
  if (!workflow) {
    throw new RepoCartographerError('unparseable_lean_json', d.text.slice(0, 200));
  }

  return {
    workflow,
    providerKind: d.providerKind ?? 'unknown',
    model: d.model ?? '',
    usage: {
      inputTokens: d.usage?.inputTokens ?? 0,
      outputTokens: d.usage?.outputTokens ?? 0,
    },
    grounded: d.grounded ?? null,
  };
}
